import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calendar, ArrowRight, ChevronLeft } from 'lucide-react';
import { newsData, newsCategories, type NewsItem } from '../data/news';

export default function News() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);

  const newsId = searchParams.get('id');

  useEffect(() => {
    const category = searchParams.get('category');
    if (category) {
      setSelectedCategory(category);
    }
  }, [searchParams]);

  useEffect(() => {
    if (newsId) {
      const news = newsData.find((n) => n.id === newsId);
      if (news) {
        setSelectedNews(news);
      }
    }
  }, [newsId]);

  const filteredNews = useMemo(() => {
    let filtered = selectedCategory === 'all' 
      ? [...newsData] 
      : newsData.filter((n) => n.category === selectedCategory);
    // 按日期降序排序（最新的在前）
    return filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [selectedCategory]);

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
    if (categoryId === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', categoryId);
    }
    searchParams.delete('id');
    setSearchParams(searchParams);
    setSelectedNews(null);
  };

  const openNewsDetail = (news: NewsItem) => {
    setSelectedNews(news);
    searchParams.set('id', news.id);
    setSearchParams(searchParams);
  };

  const closeNewsDetail = () => {
    setSelectedNews(null);
    searchParams.delete('id');
    setSearchParams(searchParams);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'company':
        return 'bg-deep-lake text-white';
      case 'sponsor':
        return 'bg-dawn-orange text-white';
      case 'industry':
        return 'bg-warm-gold text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  return (
    <main className="pt-20">
      <section className="py-16 md:py-24 bg-gradient-to-br from-deep-lake to-deep-lake-dark">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif-zh font-bold text-white mb-6">
            新闻资讯
          </h1>
          <p className="text-lg md:text-xl text-white/80 max-w-3xl mx-auto">
            了解民间艺人最新动态，掌握行业前沿资讯
          </p>
        </div>
      </section>

      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {!selectedNews ? (
            <>
              <div className="flex flex-wrap gap-2 mb-8">
                {newsCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryChange(category.id)}
                    className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                      selectedCategory === category.id
                        ? 'bg-deep-lake text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredNews.map((news) => (
                  <article
                    key={news.id}
                    onClick={() => openNewsDetail(news)}
                    className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 cursor-pointer border border-gray-100"
                  >
                   <div className="aspect-[16/10] overflow-hidden relative">
                      <img
                        src={news.image}
                        alt={news.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute top-4 left-4">
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${getCategoryColor(news.category)}`}>
                          {news.categoryName}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {news.date}
                        </span>
                      </div>
                      <h3 className="text-lg font-serif-zh font-bold text-gray-900 mb-3 group-hover:text-deep-lake transition-colors duration-300 line-clamp-2">
                        {news.title}
                      </h3>
                      <p className="text-sm text-gray-600 line-clamp-3 mb-4">
                        {news.summary}
                      </p>
                      <div className="flex items-center text-deep-lake font-medium text-sm group-hover:text-dawn-orange transition-colors duration-300">
                        阅读更多
                        <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300" />
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </>
          ) : (
            <div className="max-w-4xl mx-auto animate-fade-in">
              <button
                onClick={closeNewsDetail}
                className="flex items-center gap-2 text-gray-600 hover:text-deep-lake transition-colors duration-300 mb-6"
              >
                <ChevronLeft className="w-5 h-5" />
                返回列表
              </button>

              <article className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100">
                <div className="aspect-[21/9] overflow-hidden">
                  <img
                    src={selectedNews.image}
                    alt={selectedNews.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${getCategoryColor(selectedNews.category)}`}>
                      {selectedNews.categoryName}
                    </span>
                    <span className="flex items-center gap-1 text-sm text-gray-500">
                      <Calendar className="w-4 h-4" />
                      {selectedNews.date}
                    </span>
                  </div>
                  <h1 className="text-2xl md:text-3xl font-serif-zh font-bold text-gray-900 mb-6">
                    {selectedNews.title}
                  </h1>
                  <div className="prose prose-lg max-w-none text-gray-600 leading-relaxed">
                    {selectedNews.content.split('\n\n').map((paragraph, index) => (
                      <p key={index} className="mb-4">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </article>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
